// ===========================================
// Prompt Builder - AI-Powered Prompt Creation
// ===========================================
// Chat-based AI assistant for brainstorming prompts
// 
// FLOW:
// 1. (Optional) Select a category for context
// 2. Add custom instruction
// 3. Generate or just chat directly
// 4. Use "I want to" buttons to guide conversation
// 5. Reformat/refine the output
// 6. Apply to your prompt

import React, { useState, useRef, useEffect } from 'react';
import { promptExpertAPI, projectAPI } from '../services/api';
import { useProjects } from '../context/ProjectContext';
import {
  X,
  Bot,
  User,
  Send,
  Loader,
  Wand2,
  Check,
  Copy,
  Trash2,
  ChevronRight,
  ChevronDown,
  // Reformat icons
  Expand,
  Minimize2,
  List,
  FileText,
  Layers,
  MessageSquare,
  GitBranch,
  ClipboardList, // Task Brief icon
  // Enrich icons
  FolderOpen,
  Sparkles,
  // Intent icons
  Hammer,
  Eye,
  Zap,
  Bug,
  RefreshCw,
  TestTube,
  Lightbulb,
  FileEdit // Document icon
} from 'lucide-react';
import { 
  CATEGORIES, 
  ACTION_CATEGORIES_ARRAY, 
  ROLE_CATEGORIES_ARRAY 
} from '../data/promptCategories';
import './PromptExpertWorkbench.scss';

// ===========================================
// Intents - "I want to..." action buttons
// ===========================================
// These guide the AI conversation toward specific goals
// Each intent includes AI IDE mode recommendations based on the three-layer prompting framework
const INTENTS = [
  { 
    key: 'build', 
    label: 'Build', 
    icon: Hammer, 
    color: '#22c55e',
    message: "I want to **BUILD** something new. What patterns, structures, and considerations should my prompt include for scaffolding or implementing new features?",
    // AI IDE mode recommendation
    recommendedMode: 'Agent',
    modeRationale: 'Agent mode for multi-file creation and scaffolding'
  },
  { 
    key: 'review', 
    label: 'Review', 
    icon: Eye, 
    color: '#3b82f6',
    message: "I want to **REVIEW** code. What should I ask the AI to look for? Give me a framework for code review prompts.",
    recommendedMode: 'Ask',
    modeRationale: 'Ask mode for read-only analysis'
  },
  { 
    key: 'optimize', 
    label: 'Optimize', 
    icon: Zap, 
    color: '#f59e0b',
    message: "I want to **OPTIMIZE** for performance. What are common bottlenecks and how should I structure prompts to identify and fix them?",
    recommendedMode: 'Plan',
    modeRationale: 'Plan mode to evaluate options first'
  },
  { 
    key: 'debug', 
    label: 'Debug', 
    icon: Bug, 
    color: '#ef4444',
    message: "I want to **DEBUG** an issue. What's a systematic approach? Help me craft diagnostic prompts.",
    recommendedMode: 'Ask',
    modeRationale: 'Ask mode to explore hypotheses'
  },
  { 
    key: 'refactor', 
    label: 'Refactor', 
    icon: RefreshCw, 
    color: '#8b5cf6',
    message: "I want to **REFACTOR** code. What refactoring patterns and safety considerations should my prompt address?",
    recommendedMode: 'Plan',
    modeRationale: 'Plan mode to review changes before applying'
  },
  { 
    key: 'test', 
    label: 'Test', 
    icon: TestTube, 
    color: '#14b8a6',
    message: "I want to **TEST** my code. What testing strategies and coverage patterns should my prompt request?",
    recommendedMode: 'Agent',
    modeRationale: 'Agent mode to create test files'
  },
  { 
    key: 'document', 
    label: 'Document', 
    icon: FileEdit, 
    color: '#ec4899',
    message: "I want to **DOCUMENT** code or APIs. Help me craft prompts for comprehensive, maintainable documentation.",
    recommendedMode: 'Agent',
    modeRationale: 'Agent mode to create doc files'
  },
  { 
    key: 'explain', 
    label: 'Explain', 
    icon: Lightbulb, 
    color: '#eab308',
    message: "I want to **UNDERSTAND** this better. Help me write prompts that get clear explanations and mental models.",
    recommendedMode: 'Ask',
    modeRationale: 'Ask mode for learning without changes'
  }
];

// ===========================================
// Reformat Actions - Transform AI responses
// ===========================================
// Based on the three-layer prompting framework for AI IDEs
const REFORMAT_ACTIONS = [
  { 
    key: 'task-brief', 
    label: 'Task Brief', 
    icon: ClipboardList,
    // Task Brief template based on the three-layer prompting framework
    prompt: `Structure our conversation as a Cursor Task Brief with these sections:

# TASK
<one sentence goal>

## CONTEXT TO USE
- @Files: <specific file paths>
- @Folders: <module folders>

## CURRENT BEHAVIOR (if fixing/changing)
## DESIRED BEHAVIOR

## SCOPE
IN SCOPE: (what to change)
OUT OF SCOPE / NON-GOALS: (what NOT to touch)

## CONSTRAINTS (tech, style, safety)
## ACCEPTANCE CRITERIA (testable checkboxes)
## WORKING STYLE (plan-first, minimal diffs, etc.)

Output only the Task Brief in a code block.`
  },
  { 
    key: 'synthesize', 
    label: 'Synthesize', 
    icon: Layers, 
    prompt: 'Synthesize our conversation into a single, optimized prompt. Output only the final prompt in a code block.' 
  },
  { 
    key: 'multistep', 
    label: 'Multi-Step', 
    icon: GitBranch, 
    prompt: 'Structure this as a multi-step prompt workflow for an AI IDE.' 
  },
  { 
    key: 'prompt-only', 
    label: 'Prompt Only', 
    icon: FileText, 
    prompt: 'Output ONLY the final prompt in a code block. No explanation - just the prompt ready to use.' 
  },
  { 
    key: 'expand', 
    label: 'Expand', 
    icon: Expand, 
    prompt: 'Expand with more detail, examples, and edge cases.' 
  },
  { 
    key: 'sayless', 
    label: 'Say Less', 
    icon: Minimize2, 
    prompt: 'Make it more concise. Remove redundancy, keep core intent.' 
  },
  { 
    key: 'bullets', 
    label: 'Bullets', 
    icon: List, 
    prompt: 'Reformat as clear bullet points.' 
  },
  { 
    key: 'twosentences', 
    label: '2 Lines', 
    icon: MessageSquare, 
    prompt: 'Distill to just 2 powerful, actionable sentences.' 
  }
];

// ===========================================
// Project Status Filter Options
// ===========================================
const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'idea', label: 'Idea' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Done' }
];

// ===========================================
// Build Project Context Prompt
// ===========================================
// Uses AI-generated readmeSummary + enrichedContext when available
// Falls back to basic metadata + README excerpt if not
const buildProjectContext = (project) => {
  let parts = [];
  
  // ===========================================
  // Priority 1: Use AI-Generated README Summary
  // ===========================================
  // Condensed overview with key topics - perfect for quick context
  if (project.readmeSummary) {
    parts.push(`**📋 README Summary:**\n${project.readmeSummary}`);
  }
  
  // ===========================================
  // Priority 2: Include AI-Generated Enriched Context
  // ===========================================
  // This provides architecture, patterns, key differentiators, and security model
  // Much more useful for prompt context than raw metadata
  if (project.enrichedContext) {
    // If we have summary, add a separator before enriched context
    if (parts.length > 0) {
      parts.push('\n---\n');
    }
    parts.push(project.enrichedContext);
    parts.push(`\n*Use this context to tailor prompts for this project's codebase.*`);
    return parts.join('\n');
  }
  
  // ===========================================
  // Fallback: Build basic context from metadata
  // ===========================================
  // Used when enrichedContext hasn't been generated yet (no README uploaded)
  const tech = project.techStack?.map(t => t.name).join(', ') || 'Not specified';
  
  // If we don't have summary either, start fresh with project name
  if (parts.length === 0) {
    parts.push(`**📁 Project: ${project.name}**`);
  }
  
  if (project.description) {
    parts.push(`**Description:** ${project.description}`);
  }
  if (project.techStack?.length > 0) {
    parts.push(`**Tech Stack:** ${tech}`);
  }
  // Include README excerpt as fallback if no enrichedContext or summary
  if (project.readmeContent && !project.readmeSummary) {
    const excerpt = project.readmeContent.substring(0, 1200);
    const truncated = project.readmeContent.length > 1200 ? '...' : '';
    parts.push(`\n**README:**\n\`\`\`\n${excerpt}${truncated}\n\`\`\``);
  }
  
  parts.push(`\n*Use this context to tailor prompts for this project's codebase.*`);
  return parts.join('\n');
};

// ===========================================
// Main Component
// ===========================================
const PromptExpertWorkbench = ({
  isOpen,
  onClose,
  onApply,
  currentContent,
  currentRole,      // Primary: engineering role (Frontend, Backend, etc.)
  currentAction     // Secondary: action type (Generate, Debug, etc.)
}) => {
  // Project context for Enrich feature
  const { projects, fetchProjects, loading: projectsLoading } = useProjects();
  
  // ===========================================
  // State
  // ===========================================
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Role + Action selection (optional) - two-dimensional filtering
  const [selectedRole, setSelectedRole] = useState(null);       // e.g., 'frontend', 'backend', 'aiEngineer'
  const [selectedAction, setSelectedAction] = useState(null);   // e.g., 'analyze', 'generate', 'debug'
  
  // Selected intent for mode recommendation display
  const [selectedIntent, setSelectedIntent] = useState(null);
  
  // Custom instruction
  const [customInstruction, setCustomInstruction] = useState('');
  
  // Clipboard feedback
  const [copiedId, setCopiedId] = useState(null);
  
  // Enrich status filter
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Apply loading state
  const [applyingMetadata, setApplyingMetadata] = useState(false);
  
  // Context suggestions based on role + intent
  const [contextSuggestions, setContextSuggestions] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ===========================================
  // Auto-scroll to latest message
  // ===========================================
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ===========================================
  // Initialize on open
  // ===========================================
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      
      // Show welcome message
      if (messages.length === 0) {
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: `🔧 **Prompt Builder**

I'll help you brainstorm and refine prompts for AI IDEs like Cursor.

**How to use:**
1. Pick a **Role** (engineering perspective) and/or **Action** (what to do)
2. Add extra guidance in the text box
3. Hit **Generate** to start
4. **Reformat** responses to get the perfect prompt
5. Click **Apply** to use it

${currentContent ? "I see existing content - I can help enhance it!" : "What are we building?"}`
        }]);
      }
      
      // Fetch projects for Enrich
      if (projects.length === 0 && !projectsLoading) {
        fetchProjects();
      }
    }
  }, [isOpen, messages.length, currentContent, projects.length, projectsLoading, fetchProjects]);

  // ===========================================
  // Handlers
  // ===========================================
  
  // Clear chat and reset state
  const handleClear = () => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: `🔧 **Prompt Builder**

I'll help you brainstorm and refine prompts for AI IDEs like Cursor.

**How to use:**
1. Pick a **Role** (engineering perspective) and/or **Action** (what to do)
2. Add extra guidance in the text box
3. Hit **Generate** to start
4. **Reformat** responses to get the perfect prompt
5. Click **Apply** to use it

${currentContent ? "I see existing content - I can help enhance it!" : "What are we building?"}`
    }]);
    setSelectedRole(null);
    setSelectedAction(null);
    setSelectedIntent(null);
    setContextSuggestions(null);
    setCustomInstruction('');
    setInput('');
  };

  // Copy message to clipboard
  const handleCopy = async (message) => {
    try {
      // Extract code block if present
      const codeMatch = message.content.match(/```(?:\w+)?\n([\s\S]*?)```/);
      const text = codeMatch ? codeMatch[1].trim() : message.content;
      
      await navigator.clipboard.writeText(text);
      setCopiedId(message.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Toggle role selection (one role at a time)
  const handleRoleClick = (roleKey) => {
    setSelectedRole(prev => prev === roleKey ? null : roleKey);
  };

  // Toggle action selection (one action at a time)
  const handleActionClick = (actionKey) => {
    setSelectedAction(prev => prev === actionKey ? null : actionKey);
  };

  // Send intent message and track selected intent for mode display
  const handleIntentClick = (intent) => {
    setSelectedIntent(intent);
    sendMessage(intent.message);
  };
  
  // ===========================================
  // Fetch Context Suggestions when Role/Intent Change
  // ===========================================
  useEffect(() => {
    const fetchContextSuggestions = async () => {
      // Map role keys to the backend expected format
      const roleMapping = {
        'frontend': 'frontend',
        'backend': 'backend',
        'fullStack': 'fullStack',
        'architecture': 'architecture',
        'devops': 'devops',
        'security': 'security',
        'aiEngineer': 'ai'
      };
      
      // Map action keys to intent keys
      const intentMapping = {
        'analyze': 'review',
        'generate': 'build',
        'refactor': 'refactor',
        'debug': 'troubleshoot',
        'optimize': 'optimize',
        'test': 'test',
        'document': 'document',
        'review': 'review'
      };
      
      const role = roleMapping[selectedRole] || selectedRole;
      const intent = selectedIntent?.key || intentMapping[selectedAction] || selectedAction;
      
      if (role && intent) {
        try {
          const response = await promptExpertAPI.getContextSuggestions({ role, intent });
          if (response?.data?.success) {
            setContextSuggestions(response.data.data);
          }
        } catch (err) {
          // Non-critical - silently fail
          console.warn('Failed to fetch context suggestions:', err);
        }
      } else {
        setContextSuggestions(null);
      }
    };
    
    fetchContextSuggestions();
  }, [selectedRole, selectedAction, selectedIntent]);

  // Send reformat action
  const handleReformatClick = (action) => {
    sendMessage(action.prompt, true);
  };

  // Inject project context
  // ===========================================
  // Inject project context (enriched or fallback)
  // ===========================================
  // Fetches full project data to get enrichedContext field
  const handleEnrichClick = async (project) => {
    try {
      // Fetch full project data (list excludes enrichedContext for performance)
      const response = await projectAPI.get(project._id || project.id);
      const fullProject = response.data.data.project;
      
      // Build and send context (uses enrichedContext if available)
      sendMessage(buildProjectContext(fullProject));
    } catch (err) {
      console.error('Failed to fetch project for enrichment:', err);
      // Fall back to using cached project data (without enrichedContext)
      sendMessage(buildProjectContext(project));
    }
  };

  // ===========================================
  // Generate Initial Prompt from Selections
  // ===========================================
  // Builds structured context pillars to frame the conversation:
  // - Role pillar: engineering perspective (Frontend, Backend, etc.)
  // - Action pillar: what you want to accomplish (Generate, Debug, etc.)
  // - User's goal: the specific thing they want to accomplish
  const handleGenerate = () => {
    if (loading) return;
    
    // Get selected role and action info from CATEGORIES
    const role = selectedRole ? CATEGORIES[selectedRole] : null;
    const action = selectedAction ? CATEGORIES[selectedAction] : null;
    
    // Build structured context blocks (pillars)
    let contextParts = [];
    
    // Role pillar - engineering perspective
    if (role) {
      contextParts.push(`**Role: ${role.label}** — ${role.description}`);
    }
    
    // Action pillar - what you want to accomplish
    if (action) {
      contextParts.push(`**Action: ${action.label}** — ${action.description}`);
    }
    
    // Build the full prompt with clear structure
    let prompt;
    
    if (contextParts.length > 0 || customInstruction.trim()) {
      // Start with role framing
      prompt = `You're an expert prompt engineer for AI IDEs like Cursor.\n\n`;
      
      // Add pillar context if any selected
      if (contextParts.length > 0) {
        prompt += `**Context Pillars:**\n${contextParts.join('\n')}\n\n`;
      }
      
      // User's specific goal is the primary focus
      if (customInstruction.trim()) {
        prompt += `**Goal:** ${customInstruction.trim()}\n\n`;
      }
      
      // Instruction to the AI - varies based on what was selected
      const roleContext = role ? `from a ${role.label} perspective` : '';
      const actionContext = action ? action.label.toLowerCase() : 'task';
      prompt += `Help me craft an effective prompt${roleContext}. What patterns, structure, and considerations should I include for this ${actionContext}?`;
    } else {
      // Fallback when nothing selected - general brainstorming mode
      prompt = "You're an expert prompt engineer - help me brainstorm prompts for an AI IDE like Cursor. What are the key patterns, considerations, and structures I should include?";
    }
    
    sendMessage(prompt);
  };

  // ===========================================
  // Send Message to AI
  // ===========================================
  const sendMessage = async (messageText, isReformat = false) => {
    if (!messageText?.trim() || loading) return;

    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText.trim(),
      isReformat
    };

    setMessages(prev => [...prev, userMessage]);
    if (!isReformat) setInput('');
    setLoading(true);

    try {
      // Build conversation history (last 10 messages)
      const history = messages
        .filter(m => m.id !== 'welcome')
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      // Call API - pass role and action for context
      const response = await promptExpertAPI.chat({
        message: messageText,
        conversationHistory: history,
        context: {
          currentPrompt: currentContent ? { content: currentContent } : null,
          targetRole: selectedRole || currentRole,
          targetAction: selectedAction || currentAction,
          selectedRole: selectedRole,
          selectedAction: selectedAction
        }
      });

      if (!response?.data?.success || !response?.data?.data?.message) {
        throw new Error(response?.data?.error || 'No response from AI');
      }

      // Add AI response
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.data.message
      }]);
    } catch (err) {
      console.error('Chat error:', err);
      const errorMsg = err?.response?.data?.error || err?.message || "AI unavailable";
      
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Something went wrong: ${errorMsg}. Try again.`,
        isError: true
      }]);
    }

    setLoading(false);
  };

  // ===========================================
  // Apply Generated Prompt to Form
  // ===========================================
  const handleApply = async () => {
    // Find last AI message (not welcome)
    const lastAiMsg = [...messages]
      .reverse()
      .find(m => m.role === 'assistant' && m.id !== 'welcome');
    
    if (!lastAiMsg) return;
    
    // Extract code block or use full content
    const codeMatch = lastAiMsg.content.match(/```(?:\w+)?\n([\s\S]*?)```/);
    const content = codeMatch ? codeMatch[1].trim() : lastAiMsg.content;
    
    // Determine role and action (prefer selections, fall back to parent props)
    const role = selectedRole || currentRole || 'fullStack';
    const action = selectedAction || currentAction || 'generate';
    
    // Try to generate metadata
    let metadata = { description: '', tags: [] };
    setApplyingMetadata(true);
    
    try {
      const response = await promptExpertAPI.generateMetadata({ content, role, action });
      metadata = response.data.data || { description: '', tags: [] };
    } catch (err) {
      console.warn('Metadata generation failed:', err);
    }
    
    setApplyingMetadata(false);
    
    // Pass to parent with role + action
    onApply({
      content,
      role,
      action,
      description: metadata.description,
      tags: metadata.tags
    });
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // ===========================================
  // Render
  // ===========================================
  if (!isOpen) return null;

  // Filter projects by status
  const filteredProjects = projects.filter(p => 
    statusFilter === 'all' || p.status === statusFilter
  );

  return (
    <div className="workbench-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="workbench">
        {/* ===========================================
            Header
        =========================================== */}
        <div className="workbench__header">
          <div className="workbench__title">
            <Wand2 size={18} />
            <span>Prompt Builder</span>
          </div>
          <div className="workbench__header-actions">
            <button 
              className="workbench__clear-btn"
              onClick={handleClear}
              title="Clear chat"
              disabled={messages.length <= 1}
            >
              <Trash2 size={16} />
            </button>
            <button className="workbench__close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* ===========================================
            Roles - Engineering perspective (Full-Stack, Frontend, Backend, etc.)
        =========================================== */}
        <div className="workbench__roles-row">
          <span className="workbench__roles-label">Roles</span>
          <div className="workbench__roles-list">
            {ROLE_CATEGORIES_ARRAY.map((role) => {
              const Icon = role.icon;
              const isSelected = selectedRole === role.key;
              return (
                <button
                  key={role.key}
                  className={`workbench__category ${isSelected ? 'active' : ''}`}
                  onClick={() => handleRoleClick(role.key)}
                  style={{ '--category-color': role.color }}
                  title={role.description}
                >
                  <Icon size={12} />
                  <span>{role.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ===========================================
            Actions - What you want to accomplish (analyze, generate, debug, etc.)
        =========================================== */}
        <div className="workbench__actions-row">
          <span className="workbench__actions-label">Actions</span>
          <div className="workbench__actions-list">
            {ACTION_CATEGORIES_ARRAY.map((action) => {
              const Icon = action.icon;
              const isSelected = selectedAction === action.key;
              return (
                <button
                  key={action.key}
                  className={`workbench__category ${isSelected ? 'active' : ''}`}
                  onClick={() => handleActionClick(action.key)}
                  style={{ '--category-color': action.color }}
                  title={action.description}
                >
                  <Icon size={12} />
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ===========================================
            Custom Instruction + Generate
        =========================================== */}
        <div className="workbench__instruction">
          <textarea
            className="workbench__instruction-input"
            value={customInstruction}
            onChange={(e) => setCustomInstruction(e.target.value)}
            placeholder="What do you want to build? Add context, constraints, or questions..."
            rows={2}
            disabled={loading}
          />
          <button
            className="workbench__generate-btn"
            onClick={handleGenerate}
            disabled={loading}
            title="Start brainstorming"
          >
            {loading ? <Loader size={14} className="animate-spin" /> : <Wand2 size={14} />}
            <span>Generate</span>
          </button>
        </div>

        {/* ===========================================
            Chat Area
        =========================================== */}
        <div className="workbench__chat">
          <div className="workbench__messages">
            {messages.map((msg) => (
              <div 
                key={msg.id}
                className={`workbench__message workbench__message--${msg.role} ${msg.isReformat ? 'workbench__message--reformat' : ''} ${msg.isError ? 'workbench__message--error' : ''}`}
              >
                <div className="workbench__avatar">
                  {msg.role === 'assistant' ? <Bot size={14} /> : <User size={14} />}
                </div>
                <div className="workbench__content">
                  {/* Basic markdown rendering */}
                  <div dangerouslySetInnerHTML={{ 
                    __html: msg.content
                      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                      .replace(/`([^`]+)`/g, '<code>$1</code>')
                      .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
                      .replace(/\n/g, '<br>')
                  }} />
                  
                  {/* Copy button for AI messages */}
                  {msg.role === 'assistant' && msg.id !== 'welcome' && (
                    <button
                      className={`workbench__copy-btn ${copiedId === msg.id ? 'copied' : ''}`}
                      onClick={() => handleCopy(msg)}
                      title="Copy to clipboard"
                    >
                      {copiedId === msg.id ? (
                        <><Check size={12} /><span>Copied!</span></>
                      ) : (
                        <><Copy size={12} /><span>Copy</span></>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {loading && (
              <div className="workbench__message workbench__message--assistant">
                <div className="workbench__avatar"><Bot size={14} /></div>
                <div className="workbench__typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Chat input */}
          <div className="workbench__input-area">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything or describe what you need..."
              disabled={loading}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              className="workbench__send"
            >
              {loading ? <Loader size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>

        {/* ===========================================
            Enrich - Project Context
        =========================================== */}
        <div className="workbench__enrich">
          <span className="workbench__enrich-label">
            <Sparkles size={12} />
            Enrich:
          </span>
          
          <div className="workbench__enrich-filter">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="workbench__enrich-select"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={10} className="workbench__enrich-select-icon" />
          </div>
          
          <div className="workbench__enrich-projects">
            {projectsLoading ? (
              <span className="workbench__enrich-loading">Loading...</span>
            ) : filteredProjects.length === 0 ? (
              <span className="workbench__enrich-empty">No projects</span>
            ) : (
              filteredProjects.map((project) => (
                <button
                  key={project._id || project.id}
                  className="workbench__enrich-project"
                  onClick={() => handleEnrichClick(project)}
                  disabled={loading}
                  title={`Inject ${project.name} context`}
                >
                  <FolderOpen size={11} />
                  <span>{project.name}</span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ===========================================
            Intents - "I want to..." buttons
        =========================================== */}
        <div className="workbench__intents">
          <span className="workbench__intents-label">
            <ChevronRight size={12} />
            <span>I want to</span>
          </span>
          <div className="workbench__intents-list">
            {INTENTS.map((intent) => {
              const Icon = intent.icon;
              const isActive = selectedIntent?.key === intent.key;
              return (
                <button
                  key={intent.key}
                  className={`workbench__intent ${isActive ? 'active' : ''}`}
                  onClick={() => handleIntentClick(intent)}
                  disabled={loading}
                  style={{ '--intent-color': intent.color }}
                  title={intent.message.replace(/\*\*/g, '')}
                >
                  <Icon size={12} />
                  <span>{intent.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ===========================================
            Mode Recommendation - Shows when intent is selected
        =========================================== */}
        {selectedIntent && (
          <div className="workbench__mode-recommendation">
            <span className={`workbench__mode-badge workbench__mode-badge--${selectedIntent.recommendedMode.toLowerCase()}`}>
              {selectedIntent.recommendedMode}
            </span>
            <span className="workbench__mode-tip">{selectedIntent.modeRationale}</span>
          </div>
        )}

        {/* ===========================================
            Context Suggestions - Shows what to attach
        =========================================== */}
        {contextSuggestions && (contextSuggestions.files?.length > 0 || contextSuggestions.folders?.length > 0) && (
          <div className="workbench__context-suggestions">
            <span className="workbench__context-label">
              <Lightbulb size={12} />
              Context to Attach:
            </span>
            {contextSuggestions.files?.length > 0 && (
              <span className="workbench__context-item">
                <FileText size={11} />
                @Files: {contextSuggestions.files.join(', ')}
              </span>
            )}
            {contextSuggestions.folders?.length > 0 && (
              <span className="workbench__context-item">
                <FolderOpen size={11} />
                @Folders: {contextSuggestions.folders.join(', ')}
              </span>
            )}
            {contextSuggestions.tips && (
              <span className="workbench__context-tip">{contextSuggestions.tips}</span>
            )}
          </div>
        )}

        {/* ===========================================
            Reformat Actions
        =========================================== */}
        <div className="workbench__reformat">
          <span className="workbench__reformat-label">Reformat:</span>
          <div className="workbench__reformat-actions">
            {REFORMAT_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  className="workbench__reformat-btn"
                  onClick={() => handleReformatClick(action)}
                  disabled={loading || messages.length < 2}
                  title={action.prompt}
                >
                  <Icon size={12} />
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ===========================================
            Footer - Apply/Cancel
        =========================================== */}
        <div className="workbench__footer">
          <button className="workbench__cancel" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="workbench__apply"
            onClick={handleApply}
            disabled={messages.length < 2 || applyingMetadata}
          >
            {applyingMetadata ? (
              <><Loader size={14} className="animate-spin" />Applying...</>
            ) : (
              <><Check size={14} />Apply to Prompt</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptExpertWorkbench;
